/** Public check of a barangay resident ID (FR-RES-06): name, photo and validity only. */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BadgeCheck, ShieldAlert } from 'lucide-react';

interface Result { valid: boolean; status?: string; reason?: string; name?: string; barangay?: string; cardNo?: string; validUntil?: string; hasPhoto?: boolean }
const WHY: Record<string, string> = { REVOKED: 'This ID was revoked (lost, replaced or cancelled).', EXPIRED: 'This ID has expired.', INACTIVE: 'The person is no longer an active resident.' };

export function VerifyIdPage(): JSX.Element {
  const { token } = useParams();
  const [r, setR] = useState<Result | null>(null);
  useEffect(() => { void fetch(`/api/v1/public/verify/id/${encodeURIComponent(token ?? '')}`).then((x) => x.json()).then(setR).catch(() => setR({ valid: false, reason: 'Could not check right now. Try again.' })); }, [token]);
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Barangay resident ID check</p>
        {!r ? <p className="mt-6 text-slate-500">Checking…</p> : (
          <>
            {r.valid ? <BadgeCheck aria-hidden="true" className="mx-auto mt-4 h-12 w-12 text-emerald-600" /> : <ShieldAlert aria-hidden="true" className="mx-auto mt-4 h-12 w-12 text-red-600" />}
            <p className={`mt-2 text-lg font-bold ${r.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{r.valid ? 'Valid ID' : 'Not valid'}</p>
            {r.hasPhoto && r.status !== 'REVOKED' && <img src={`/api/v1/public/verify/id/${encodeURIComponent(token ?? '')}/photo`} alt="Photo on the ID" className="mx-auto mt-4 h-36 w-28 rounded-xl object-cover" />}
            {r.name && <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">{r.name}</p>}
            {r.barangay && <p className="text-sm text-slate-600 dark:text-slate-300">{r.barangay}</p>}
            {r.cardNo && <p className="mt-2 text-sm text-slate-500">ID {r.cardNo} · valid until {r.validUntil}</p>}
            {!r.valid && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{r.reason ?? WHY[r.status ?? ''] ?? ''}</p>}
          </>
        )}
      </div>
    </main>
  );
}
